import React, { useEffect, useState } from 'react';
import { useMachine } from '@xstate/react';
import { Seo } from '@quantum/seo';
import { track } from '@amplitude/analytics-browser';
import { productRecommender } from '@/machines/product-recommender';
import { ActorComponent } from '@/components/actors';
import { ErrorTemplate } from '@/templates/error';
import { ProductRecommenderStates, QuestionnaireEvent } from '@/machines/types';
import { Loading } from '@/components/loading';
import { navigateToRoute } from '@/utils';
import { SeoProps } from '@/utils/types';
import { DOMAIN_LOCALE, DOMAIN_TLD } from '@/constants/domains';
import { domainFromUrl, hostUrl } from '@/helpers/domainUrl';

interface IndexPageProps {
  readonly pageContext: {
    questionnaireName: string;
  };
}

const Assessment: React.FC<IndexPageProps> = ({ pageContext: { questionnaireName } }) => {
  const seoProps = ({ link }: { link: string }): SeoProps => ({
    slug: 'slug',
    tld: DOMAIN_TLD[domainFromUrl()],
    node_locale: DOMAIN_LOCALE[domainFromUrl()],
    metaTitle: 'metaTitle',
    metaDescription: {
      metaDescription: 'metaDescription',
    },
    index: false,
    autoGenerateJsonLd: false,
    canonicalUrl: `https://${hostUrl}/${link}/`,
    socialTitle: 'socialTitle',
    socialDescription: 'socialDescription',
    socialSiteName: 'socialSiteName',
    socialUrl: `https://${hostUrl}/${link}/`,
  });

  const [disableLoadingText, setDisableLoadingText] = useState(false);
  const [state, send] = useMachine(productRecommender);
  const { generalHealth } = state.children;

  useEffect(() => {
    if (state.done) {
      track('quiz ended');
      navigateToRoute('/recommendation/');
    }
  }, [state.done]);

  if (state.matches(ProductRecommenderStates.failed)) {
    return (
      <>
        <Seo
          seoDetails={seoProps({ link: questionnaireName }) as never}
          location={`https://${hostUrl}/${questionnaireName}/`}
          pageType='pageType'
          lang={DOMAIN_LOCALE[domainFromUrl()]}
        />
        <ErrorTemplate
          header='Sorry, we broke something'
          description='Please restart the quiz to get your personalised test suggestions.'
          ctaText='Restart quiz'
          onRetry={() => {
            setDisableLoadingText(true);
            send(QuestionnaireEvent.RETRY);
          }}
        />
      </>
    );
  }

  return (
    <>
      {generalHealth && <ActorComponent questionnaireName={questionnaireName} actor={generalHealth} />}
      {(state.matches(ProductRecommenderStates.submitQuestionnaire) ||
        state.matches(ProductRecommenderStates.done)) && (
        <Loading
          disableText={disableLoadingText}
          title='Great work!'
          description='Our clinical experts are choosing the best tests for you.'
        />
      )}
    </>
  );
};

export default Assessment;
